import { graphqlPost } from "api/graphqlClient";
import { useCallback, useEffect, useRef, useState } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import {
    recordLogListGradualNewerQuery,
    recordLogListGradualQuery,
    recordLogOldestQuery,
    recordLogListGradualOldestQuery,
    recordLogPlainListSubscription,
} from "./fetchRecordLogList";
import {
    GradualCursorInput,
    GradualLogPage,
    GradualLogRow,
    LIVE_WINDOW_LIMIT,
    MAX_NEWER_CATCHUP_PAGES,
    capNewestRows,
    capOldestRows,
    mergeGradualRows,
    mergeLiveRows,
    nextCursor,
    nextNewerCursor,
    newestRowCursor,
    pageRows,
    windowFetchLimit,
} from "./gradualLog";

export interface UseGradualRecordLogsArgs {
    /** The record to scope logs to. Undefined = workspace-wide (no record scope);
     *  container scoping is expressed through `filter` instead. */
    recordId?: string;
    /** The container (project / bundle / other container record) whose logs the
     *  `filter` semijoin selects. Passed as the gradual endpoint's `container_id`
     *  argument: the server floors the walk at the earliest created_at of the
     *  container and its member records, so a sparse container's scan ends there
     *  instead of walking back to the retention horizon. Adds no row filter.
     *  Undefined for single-record and workspace-wide logs. */
    containerId?: string;
    /** The assembled filter JSON string (levels, types, container semijoin, …),
     *  or null for no filter. */
    filter: string | null;
    /** How many rows one gesture loads (the count-mode `count`): the initial load
     *  fetches up to this many newest rows, and each loadOlder() fetches up to this
     *  many more older rows via the continuation cursor. Sourced from the table's
     *  rows-per-page; never hardcode (Stefan, 2026-09-25). Clamped to the backend's
     *  per-call ceiling (RECORD_LOG_GRADUAL_COUNT_MAX). */
    pageSize: number;
    /** When false, no fetching happens (e.g. live-updates toggled off). */
    enabled?: boolean;
    /** Gate for the live row SUBSCRIPTION only (independent of the gradual fetch):
     *  when false the live websocket is not opened / is torn down, while
     *  loadOlder() and the already-loaded rows are untouched. The Logs tab sets
     *  this to "scrolled to the top" so live updates stream only while the user is
     *  viewing the newest rows (Stefan, 2026-10-12, items 2 & 3) — new rows always
     *  land at the top, so subscribing while scrolled into history would merge rows
     *  the user cannot see and keep a socket open needlessly. On re-subscribe the
     *  subscription's newest-window push catches up anything that arrived while
     *  detached. Defaults to true (always subscribe while enabled). Only gates the
     *  subscription, never the fetch, so older-log loading keeps working when
     *  scrolled away from the top. See operator-task-fe-logs-tab-bounded-dom-virtualization
     *  (leg D) / operator-task-fe-logs-tab-live-row-updates. */
    liveSubscribe?: boolean;
    /** Cap on how many rows are kept in memory. When the accumulated list exceeds
     *  this while the user is at the top (live updates streaming / on return to the
     *  top), the OLDEST rows beyond the cap are discarded and the older-continuation
     *  cursor is reset so they re-load contiguously on scroll-down — bounding memory
     *  and keeping the virtual-list scrollbar handle usable (Stefan, 2026-10-15).
     *  While the user is scrolled into history (liveSubscribe false), loading older
     *  rows past the cap instead drops the NEWEST rows from the top; the forward
     *  catch-up re-loads them via `continue_after` on return to the top
     *  (operator-task-fe-logs-tab-load-newer-to-newest-before-subscribe).
     *  0 / undefined = uncapped. See operator-task-fe-logs-tab-bounded-dom-virtualization. */
    maxRows?: number;
}

export interface UseGradualRecordLogsResult {
    rows: GradualLogRow[];
    /** True while a page is actively being fetched. */
    isScanning: boolean;
    /** True while the forward catch-up (loaded top row -> true newest row) runs on
     *  return to the top, before the live subscription opens. */
    isLoadingNewer: boolean;
    /** True after the user cancelled the forward catch-up (cancelNewer). The live
     *  subscription stays closed until resumeNewer() or the user scrolls away from
     *  the top and back. */
    isNewerPaused: boolean;
    /** Increments each time the newest rows are dropped from the top while the
     *  user is deep in history, so the caller can shift its scroll position by the
     *  removed height and keep the row being read in place. */
    newestEvictedEpoch: number;
    /** Increments each time newer rows are prepended above the row the user is
     *  reading (a page loaded while browsing history), so the caller can grow its
     *  scroll position by the added height and keep that row in place. */
    newerPrependEpoch: number;
    /** How many newer rows were loaded above the user's place since they last saw
     *  the newest rows. Drives the "N newer logs — Go to newest" bar. */
    newerAddedCount: number;
    /** Increments when goToOldest() has loaded the oldest rows, so the caller can
     *  scroll to the bottom of the list. */
    oldestLoadedEpoch: number;
    /** True when older history remains beyond what has been loaded. */
    moreAvailable: boolean;
    /** Oldest instant the server walk reached so far (how far back history has been
     *  scanned), or null. */
    scannedThrough: string | null;
    /** True when the last page stopped on the server-side safety cap (so more rows
     *  remain and a further loadOlder() resumes), rather than reaching the floor. */
    capped: boolean;
    error: Error | null;
    /** Load the next (older) screenful of up to `pageSize` rows via the continuation
     *  cursor, appending them to the accumulated list. No-op at the history floor. */
    loadOlder: () => void;
    /** Stop the in-flight older-page fetch (the bottom "loading older" Cancel),
     *  keeping the rows loaded so far. The forward catch-up has its own
     *  cancelNewer(). */
    cancel: () => void;
    /** Discard everything and reload from the newest rows. */
    reload: () => void;
    /** Stop the in-flight forward catch-up only (the top "loading newer" Cancel),
     *  keeping the rows loaded so far, and pause it until resumeNewer(). */
    cancelNewer: () => void;
    /** Restart the forward catch-up after cancelNewer(). */
    resumeNewer: () => void;
    /** Drop the loaded rows and load the newest ones; the live subscription opens
     *  once the caller is back at the top. The caller scrolls to the top. */
    goToNewest: () => void;
    /** Drop the loaded rows and load the oldest ones (end of logs at the bottom).
     *  Scrolling up then loads newer pages, one per return to the top, until the
     *  newest row; only then do live updates resume. The caller scrolls to the
     *  bottom when oldestLoadedEpoch increments. */
    goToOldest: () => void;
}

//: Hard per-call ceiling the backend clamps `count` to (resources/record_log.py
//: RECORD_LOG_GRADUAL_COUNT_MAX). Clamp on the client too so a degenerate large
//: rows-per-page is sent as a sensible value.
const RECORD_LOG_GRADUAL_COUNT_MAX = 1000;

/**
 * Fetches record logs through the partition-pruned gradual endpoint in COUNT mode:
 * each call asks the backend for up to `pageSize` rows and the server walks daily
 * partitions newest→older itself (keyset-paged) until it has that many rows,
 * reaches the record/caller created_at floor (clean end-of-logs), or trips its own
 * safety cap — returning the rows plus a `continue_before` cursor and a `capped`
 * flag in one round-trip. The initial load fetches the newest `pageSize` rows; each
 * loadOlder() fetches the next older `pageSize` rows via the cursor and appends
 * them. No total COUNT(*) is ever issued (that is the full-partition scan this
 * replaces).
 *
 * Async fetches live in a hook (mirroring useContainerDashboards) rather than a
 * saga because the accumulation/cursor state is local to the log panel and the
 * classic path this replaces also fetched directly in the component.
 */
export function useGradualRecordLogs({
    recordId,
    containerId,
    filter,
    pageSize,
    enabled = true,
    liveSubscribe = true,
    maxRows = 0,
}: UseGradualRecordLogsArgs): UseGradualRecordLogsResult {
    const [rows, setRows] = useState<GradualLogRow[]>([]);
    const [isScanning, setIsScanning] = useState(false);
    const [moreAvailable, setMoreAvailable] = useState(false);
    const [scannedThrough, setScannedThrough] = useState<string | null>(null);
    const [capped, setCapped] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [isLoadingNewer, setIsLoadingNewer] = useState(false);
    const [isNewerPaused, setIsNewerPaused] = useState(false);
    // False once the user has scrolled away from the top: rows written while the
    // live subscription was closed may be missing between the loaded top row and
    // the true newest row. Returning to the top first pages FORWARD through that
    // gap (continue_after) and only then opens the live subscription (Stefan,
    // 2026-10-13). True after the initial load, which starts at the newest row.
    const [caughtUp, setCaughtUp] = useState(true);
    const newerAbortRef = useRef<AbortController | null>(null);
    const [newestEvictedEpoch, setNewestEvictedEpoch] = useState(0);
    // Browsing history (Stefan, 2026-11-02, option (a)): the user left the top
    // with rows loaded, or jumped to the oldest rows. Newer rows then load ONE
    // page per return to the top and are prepended ABOVE the row being read (the
    // caller keeps the scroll position), instead of jumping to the newest rows.
    const [browsingHistory, setBrowsingHistory] = useState(false);
    const [newerPrependEpoch, setNewerPrependEpoch] = useState(0);
    const [newerAddedCount, setNewerAddedCount] = useState(0);
    const [oldestLoadedEpoch, setOldestLoadedEpoch] = useState(0);
    // Re-runs the catch-up start effect after each browsing page, in case the top
    // is still in view (a short page did not push it out).
    const [newerPageTick, setNewerPageTick] = useState(0);
    // The server's forward cursor after a browsing page that stopped short of the
    // newest row; null = derive it from the loaded top row.
    const newerCursorRef = useRef<GradualCursorInput | null>(null);
    // Mirrors liveSubscribe (is the top in view) for use inside async callbacks.
    const liveSubscribeRef = useRef(liveSubscribe);
    liveSubscribeRef.current = liveSubscribe;
    // Set by goToNewest(): the list is fresh, so leaving the top before the scroll
    // lands must not mark it as behind.
    const skipMarkBehindRef = useRef(false);
    // Set while goToOldest() loads: an empty list must not count as caught up.
    const oldestLoadingRef = useRef(false);

    // A generation counter invalidates a stale in-flight fetch when inputs change
    // or the caller reloads; the AbortController cancels the HTTP request itself.
    const genRef = useRef(0);
    const abortRef = useRef<AbortController | null>(null);
    // The continuation cursor for the NEXT (older) page, or null at the newest
    // window (initial load) / once the history floor has been reached.
    const cursorRef = useRef<GradualCursorInput | null>(null);
    const rowsRef = useRef<GradualLogRow[]>([]);
    // Generation counter for the live subscription: bumped on teardown /
    // resubscribe so a message from a stale socket (during a brief close/reopen
    // on filter change) is ignored.
    const liveGenRef = useRef(0);

    // Fetch ONE count-mode page starting at the current continuation cursor
    // (null = newest window for the initial load; a cursor = the next older
    // screenful). Appends the returned rows to the accumulated list and advances
    // the cursor. A single server call does the whole window walk, so there is no
    // client-side multi-window loop.
    const fetchPage = useCallback(async () => {
        if (!enabled) {
            return;
        }
        const gen = genRef.current;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setIsScanning(true);
        setError(null);
        const isStale = () => controller.signal.aborted || gen !== genRef.current;
        try {
            const variables: Record<string, unknown> = {
                record_id: recordId,
                container_id: containerId,
                filter,
                // Count mode: ask for a whole screenful; the server walks and
                // keyset-pages the partitions until satisfied/floored/capped.
                count: Math.max(
                    1,
                    Math.min(Math.floor(pageSize) || 1, RECORD_LOG_GRADUAL_COUNT_MAX)
                ),
                // Per-window SQL page ceiling, capped so the query cost stays under
                // the GraphQL 2000 cost cap (the count walk still reaches `count`
                // across keyset round-trips).
                limit: windowFetchLimit(pageSize),
                continue_before: cursorRef.current ?? undefined,
            };
            const response = (await graphqlPost(
                { query: recordLogListGradualQuery, variables },
                { signal: controller.signal }
            )) as { data?: { record_log_list_gradual?: GradualLogPage } };
            if (isStale()) {
                return;
            }
            const page: GradualLogPage = response?.data?.record_log_list_gradual ?? {};
            const merged = mergeGradualRows(rowsRef.current, pageRows(page));
            rowsRef.current = merged;
            cursorRef.current = nextCursor(page);
            setRows(merged);
            // `scanned_from` is the OLDEST window edge the walk reached — i.e. how
            // far back history has been scanned so far (the "scanned back to" hint).
            setScannedThrough(page.scanned_from ?? null);
            setMoreAvailable(Boolean(page.more_available) && cursorRef.current !== null);
            setCapped(Boolean(page.capped));
        } catch (err) {
            if (controller.signal.aborted || gen !== genRef.current) {
                return;
            }
            setError(err instanceof Error ? err : new Error(String(err)));
        } finally {
            if (gen === genRef.current) {
                setIsScanning(false);
            }
        }
    }, [enabled, recordId, containerId, filter, pageSize]);

    // Restart from the newest window whenever the query inputs change.
    useEffect(() => {
        genRef.current += 1;
        abortRef.current?.abort();
        abortRef.current = null;
        newerAbortRef.current?.abort();
        newerAbortRef.current = null;
        rowsRef.current = [];
        cursorRef.current = null;
        setRows([]);
        setMoreAvailable(false);
        setScannedThrough(null);
        setCapped(false);
        setError(null);
        setIsLoadingNewer(false);
        setIsNewerPaused(false);
        setCaughtUp(true);
        setBrowsingHistory(false);
        setNewerAddedCount(0);
        newerCursorRef.current = null;
        oldestLoadingRef.current = false;
        if (enabled) {
            void fetchPage();
        } else {
            setIsScanning(false);
        }
        return () => {
            abortRef.current?.abort();
        };
    }, [enabled, fetchPage]);

    const loadOlder = useCallback(() => {
        // At the history floor there is no cursor; nothing older to fetch.
        if (cursorRef.current === null) {
            return;
        }
        void fetchPage();
    }, [fetchPage]);

    const cancel = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setIsScanning(false);
    }, []);

    const reloadRef = useRef<() => void>(() => undefined);
    const reload = useCallback(() => {
        genRef.current += 1;
        newerAbortRef.current?.abort();
        newerAbortRef.current = null;
        rowsRef.current = [];
        cursorRef.current = null;
        setRows([]);
        setMoreAvailable(false);
        setScannedThrough(null);
        setCapped(false);
        setError(null);
        setIsLoadingNewer(false);
        setIsNewerPaused(false);
        setCaughtUp(true);
        setBrowsingHistory(false);
        setNewerAddedCount(0);
        newerCursorRef.current = null;
        oldestLoadingRef.current = false;
        void fetchPage();
    }, [fetchPage]);
    reloadRef.current = reload;

    const goToNewest = useCallback(() => {
        skipMarkBehindRef.current = true;
        reload();
    }, [reload]);

    // Load the OLDEST page, shown newest-first like every other page. A record-
    // or container-scoped view uses the gradual read with `from_oldest` (a
    // forward walk from the scope's creation); an unscoped view (workspace-wide,
    // bundle manager) has no floor and uses a plain ascending list read. Older history is exhausted by
    // definition, so the bottom shows the end-of-logs marker; newer pages load as
    // the user scrolls up (browsing history), and live updates resume only once
    // the newest row is reached.
    const goToOldest = useCallback(async () => {
        if (!enabled) {
            return;
        }
        genRef.current += 1;
        const gen = genRef.current;
        abortRef.current?.abort();
        newerAbortRef.current?.abort();
        newerAbortRef.current = null;
        const controller = new AbortController();
        abortRef.current = controller;
        oldestLoadingRef.current = true;
        skipMarkBehindRef.current = false;
        rowsRef.current = [];
        cursorRef.current = null;
        newerCursorRef.current = null;
        setRows([]);
        setMoreAvailable(false);
        setScannedThrough(null);
        setCapped(false);
        setError(null);
        setIsLoadingNewer(false);
        setIsNewerPaused(false);
        setCaughtUp(false);
        setBrowsingHistory(true);
        setNewerAddedCount(0);
        setIsScanning(true);
        try {
            const count = Math.max(
                1,
                Math.min(Math.floor(pageSize) || 1, RECORD_LOG_GRADUAL_COUNT_MAX)
            );
            let newestFirst: GradualLogRow[];
            if (recordId || containerId) {
                // Scoped view: forward walk from the record's / container's floor.
                const response = (await graphqlPost(
                    {
                        query: recordLogListGradualOldestQuery,
                        variables: {
                            record_id: recordId,
                            container_id: containerId,
                            filter,
                            count,
                            limit: windowFetchLimit(pageSize),
                        },
                    },
                    { signal: controller.signal }
                )) as { data?: { record_log_list_gradual?: GradualLogPage } };
                if (controller.signal.aborted || gen !== genRef.current) {
                    return;
                }
                const page: GradualLogPage = response?.data?.record_log_list_gradual ?? {};
                newestFirst = pageRows(page);
                // Resume newer pages from the server cursor (it may sit past an
                // empty day window); without one, the top row is the cursor.
                newerCursorRef.current = page.more_available ? nextNewerCursor(page) : null;
            } else {
                const response = (await graphqlPost(
                    {
                        query: recordLogOldestQuery,
                        variables: {
                            filter,
                            limit: count,
                            offset: 0,
                            order: "+created_at,+id",
                        },
                    },
                    { signal: controller.signal }
                )) as { data?: { record_log_plain_list?: GradualLogRow[] } };
                if (controller.signal.aborted || gen !== genRef.current) {
                    return;
                }
                const oldestFirst = (response?.data?.record_log_plain_list ?? []).filter(Boolean);
                newestFirst = [...oldestFirst].reverse();
            }
            rowsRef.current = newestFirst;
            setRows(newestFirst);
            setOldestLoadedEpoch((n) => n + 1);
        } catch (err) {
            if (controller.signal.aborted || gen !== genRef.current) {
                return;
            }
            setError(err instanceof Error ? err : new Error(String(err)));
        } finally {
            if (gen === genRef.current) {
                oldestLoadingRef.current = false;
                setIsScanning(false);
                setNewerPageTick((n) => n + 1);
            }
        }
    }, [enabled, filter, pageSize, recordId, containerId]);

    // Merge live-pushed newest rows into the accumulated list. Reads/writes the
    // same rowsRef the gradual fetch accumulates into, so a subsequent loadOlder()
    // merges against the live-updated set. mergeLiveRows returns the SAME array
    // when nothing new arrived, so we skip the state update (no re-render loop).
    const applyLiveRows = useCallback(
        (incoming: GradualLogRow[]) => {
            const merged = mergeLiveRows(rowsRef.current, incoming);
            if (merged === rowsRef.current) {
                // Nothing new arrived — skip the state update (no re-render loop).
                return;
            }
            // Bound the in-memory list: this path only fires while the live
            // subscription is open, i.e. the user is scrolled to the top, so it is
            // safe to discard the oldest rows beyond the cap (they re-load via the
            // reset older-cursor when the user scrolls back down).
            const capped = capNewestRows(merged, maxRows);
            rowsRef.current = capped.rows;
            setRows(capped.rows);
            if (capped.trimmed) {
                cursorRef.current = capped.olderCursor;
                // The dropped rows are older than everything retained, so older
                // history is definitely available again via the reset cursor.
                setMoreAvailable(true);
            }
        },
        [maxRows]
    );

    // Trim the in-memory list back to the cap by discarding the oldest rows and
    // resetting the older-continuation cursor so they re-load contiguously. Only
    // correct at the top (retains the newest rows); callers invoke it only on the
    // top-gated paths. A no-op (and no state update) when nothing is over the cap.
    const trimToCap = useCallback(() => {
        const capped = capNewestRows(rowsRef.current, maxRows);
        if (!capped.trimmed) {
            return;
        }
        rowsRef.current = capped.rows;
        cursorRef.current = capped.olderCursor;
        setRows(capped.rows);
        setMoreAvailable(true);
    }, [maxRows]);

    // Away from the top the live subscription is closed, so rows written from now
    // on are not merged: mark the list as possibly behind the newest row. Also
    // fires when a catch-up finishes while the user is no longer at the top (the
    // catch-up keeps the user's place, which moves the top out of view), so the
    // next return to the top pages forward again before subscribing.
    // With rows loaded this means the user is reading history: newer rows then
    // load page by page above their place (browsingHistory).
    useEffect(() => {
        if (liveSubscribe) {
            skipMarkBehindRef.current = false;
            return;
        }
        if (enabled && caughtUp && !skipMarkBehindRef.current) {
            setCaughtUp(false);
            if (rowsRef.current.length > 0) {
                setBrowsingHistory(true);
            }
        }
    }, [enabled, liveSubscribe, caughtUp]);

    // Back at the top and live again: the newer rows have been seen.
    useEffect(() => {
        if (liveSubscribe && caughtUp && newerAddedCount > 0) {
            setNewerAddedCount(0);
        }
    }, [liveSubscribe, caughtUp, newerAddedCount]);

    // A cancelled catch-up re-arms when the user scrolls away from the top and
    // back, mirroring the bottom "load older" Cancel (Stefan, 2026-10-15).
    useEffect(() => {
        if (!liveSubscribe && isNewerPaused && !isLoadingNewer) {
            setIsNewerPaused(false);
        }
    }, [liveSubscribe, isNewerPaused, isLoadingNewer]);

    // Forward catch-up on return to the top: page from the loaded top row toward
    // the newest row via `continue_after` until the server reports the newest row
    // reached, merging each page in at the top and trimming the oldest rows to the
    // in-memory cap as it goes (the user is at the top, so the newest rows are the
    // ones to keep). Only then is the list marked caught up, which opens the live
    // subscription. Bounded by MAX_NEWER_CATCHUP_PAGES; the subscription's first
    // push covers anything written between the last page and the socket opening.
    const catchUpNewer = useCallback(async () => {
        if (oldestLoadingRef.current) {
            return;
        }
        let cursor = newerCursorRef.current ?? newestRowCursor(rowsRef.current);
        if (cursor === null) {
            // Nothing loaded: the subscription's newest-window push is the catch-up.
            // Checked before an AbortController is registered: the start effect only
            // fires while newerAbortRef is empty, so a controller left behind here
            // would block every later catch-up.
            setBrowsingHistory(false);
            setCaughtUp(true);
            return;
        }
        // Browsing history: one page per return to the top, prepended above the
        // user's place. Otherwise: page to the newest row in one go.
        const keepPlace = browsingHistory;
        // Re-run the start effect after a browsing page, once the controller is
        // cleared (a setState here may render synchronously).
        let retrigger = false;
        const gen = genRef.current;
        newerAbortRef.current?.abort();
        const controller = new AbortController();
        newerAbortRef.current = controller;
        const isStale = () => controller.signal.aborted || gen !== genRef.current;
        setIsLoadingNewer(true);
        setError(null);
        try {
            const pageLimit = keepPlace ? 1 : MAX_NEWER_CATCHUP_PAGES;
            for (let i = 0; i < pageLimit && cursor !== null; i += 1) {
                const variables: Record<string, unknown> = {
                    record_id: recordId,
                    filter,
                    count: Math.max(
                        1,
                        Math.min(Math.floor(pageSize) || 1, RECORD_LOG_GRADUAL_COUNT_MAX)
                    ),
                    limit: windowFetchLimit(pageSize),
                    continue_after: cursor,
                };
                const response = (await graphqlPost(
                    { query: recordLogListGradualNewerQuery, variables },
                    { signal: controller.signal }
                )) as { data?: { record_log_list_gradual?: GradualLogPage } };
                if (isStale()) {
                    return;
                }
                const page: GradualLogPage = response?.data?.record_log_list_gradual ?? {};
                const before = rowsRef.current.length;
                const merged = mergeLiveRows(rowsRef.current, pageRows(page));
                const added = merged.length - before;
                const bounded = capNewestRows(merged, maxRows);
                if (bounded.rows !== rowsRef.current) {
                    rowsRef.current = bounded.rows;
                    setRows(bounded.rows);
                    if (keepPlace && added > 0) {
                        setNewerPrependEpoch((n) => n + 1);
                        setNewerAddedCount((n) => n + added);
                    }
                }
                if (bounded.trimmed) {
                    cursorRef.current = bounded.olderCursor;
                    setMoreAvailable(true);
                }
                cursor = page.more_available ? nextNewerCursor(page) : null;
            }
            if (keepPlace) {
                newerCursorRef.current = cursor;
                if (cursor === null) {
                    // Reached the newest row. At the top: go live now. Otherwise
                    // the next return to the top runs a quick catch-up without
                    // keeping the place (the user then wants the newest rows).
                    setBrowsingHistory(false);
                    if (liveSubscribeRef.current) {
                        setCaughtUp(true);
                    }
                }
                retrigger = true;
                return;
            }
            newerCursorRef.current = null;
            if (cursor !== null) {
                // The page bound ran out before the newest row: subscribing now
                // would leave a gap between the loaded top and the live window.
                // Jump to the newest rows instead (the user is at the top).
                reloadRef.current();
                return;
            }
            setCaughtUp(true);
        } catch (err) {
            if (isStale()) {
                return;
            }
            setError(err instanceof Error ? err : new Error(String(err)));
        } finally {
            if (!isStale()) {
                setIsLoadingNewer(false);
            }
            if (newerAbortRef.current === controller) {
                newerAbortRef.current = null;
            }
            if (retrigger && !isStale()) {
                setNewerPageTick((n) => n + 1);
            }
        }
    }, [recordId, filter, pageSize, maxRows, browsingHistory]);

    // Deep in older history (the live subscription is closed) the list grows at
    // the bottom with every older page. Past the cap, drop the newest rows from
    // the top: they are the farthest from where the user reads, and the forward
    // catch-up re-loads them on return to the top. Runs in its own render after
    // the older page is committed, so the caller sees the removal on its own and
    // can shift the scroll position by exactly the removed height.
    useEffect(() => {
        if (!enabled || liveSubscribe || isLoadingNewer) {
            return;
        }
        const bounded = capOldestRows(rows, maxRows);
        if (!bounded.trimmed) {
            return;
        }
        rowsRef.current = bounded.rows;
        newerCursorRef.current = null;
        setRows(bounded.rows);
        setCaughtUp(false);
        setNewestEvictedEpoch((n) => n + 1);
    }, [enabled, liveSubscribe, isLoadingNewer, rows, maxRows]);

    // Start the catch-up on return to the top. Once started it runs to the newest
    // row even if the top leaves the viewport meanwhile: rows are prepended while
    // the user's place is kept, which scrolls the top out of view, and aborting
    // then would stall the catch-up after its first page. It stops on cancelNewer(),
    // an input change / reload, or unmount.
    useEffect(() => {
        if (
            enabled &&
            liveSubscribe &&
            !caughtUp &&
            !isNewerPaused &&
            newerAbortRef.current === null
        ) {
            void catchUpNewer();
        }
    }, [enabled, liveSubscribe, caughtUp, isNewerPaused, catchUpNewer, newerPageTick]);
    useEffect(
        () => () => {
            newerAbortRef.current?.abort();
        },
        []
    );

    const cancelNewer = useCallback(() => {
        newerAbortRef.current?.abort();
        newerAbortRef.current = null;
        setIsLoadingNewer(false);
        setIsNewerPaused(true);
    }, []);

    const resumeNewer = useCallback(() => {
        setIsNewerPaused(false);
    }, []);

    // When the user returns to the top and the list is caught up (the live
    // subscription re-opens), trim any rows accumulated while they were scrolled
    // into older history back to the cap, even before the next live push arrives.
    useEffect(() => {
        if (enabled && liveSubscribe && caughtUp) {
            trimToCap();
        }
    }, [enabled, liveSubscribe, caughtUp, trimToCap]);

    // Live row streaming: while enabled (live-updates toggle on) AND the caller
    // has the subscription gated open (liveSubscribe — the Logs tab sets this to
    // "scrolled to the top"), subscribe to the newest record_log rows and merge
    // arrivals into the accumulated list so new lines appear at the top in real
    // time without discarding loaded older rows. Uses the no-COUNT
    // `record_log_plain_list` subscription (see fetchRecordLogList.ts), floored at
    // the current UTC day so each NOTIFY-driven push stays partition-bounded and
    // cheap. Toggling off (enabled=false), scrolling away from the top
    // (liveSubscribe=false), or a filter change closes the socket; new arrivals
    // are always newer than the floor, so the older pages are untouched, and a
    // re-subscribe pulls the newest window so nothing that arrived while detached
    // is missed.
    useEffect(() => {
        if (!enabled || !liveSubscribe || !caughtUp) {
            return undefined;
        }
        const typedWindow: any = window;
        const websocketHost =
            typedWindow.REACT_APP_WEBSOCKET_API || `wss://${typedWindow.location.host}`;
        // Prune older daily partitions: a new row is created now-or-later, so the
        // current UTC day's start is always <= any genuine arrival and safely
        // excludes historical partitions from the newest-window read.
        const floorIso = `${new Date().toISOString().slice(0, 10)}T00:00:00.000Z`;
        const floorClause = { field: "created_at", op: "gte", value: floorIso };
        let subFilter: Record<string, unknown>;
        try {
            subFilter = filter
                ? { and: [JSON.parse(filter), floorClause] }
                : { and: [floorClause] };
        } catch {
            subFilter = { and: [floorClause] };
        }
        const websocketParams = {
            subscription: recordLogPlainListSubscription,
            variables: {
                // `$filter` is a GraphQL String — send it as a JSON string, the
                // same shape the POST /graphql queries and the Executions live
                // subscription use.
                filter: JSON.stringify(subFilter),
                limit: LIVE_WINDOW_LIMIT,
                offset: 0,
                order: "-created_at,-id",
            },
        };
        const myGen = (liveGenRef.current += 1);
        let socket: ReconnectingWebSocket | null = new ReconnectingWebSocket(
            `${websocketHost}${typedWindow.REACT_APP_WEBSOCKET_API_NAMESPACE}/graphql?subscription_message=true`
        );
        const handleMessage = (event: MessageEvent) => {
            if (myGen !== liveGenRef.current) {
                return;
            }
            let response: any;
            try {
                response = JSON.parse(event.data);
            } catch {
                return;
            }
            // A live-channel error must not disturb the already-loaded view (the
            // gradual read owns error surfacing); just skip the bad push.
            if (response?.BadRequest || response?.InternalServerError || response?.errors) {
                return;
            }
            const list: unknown =
                response?.data?.record_log_plain_list ?? response?.record_log_plain_list;
            if (Array.isArray(list)) {
                applyLiveRows(list.filter(Boolean) as GradualLogRow[]);
            }
        };
        socket.addEventListener("open", () => {
            socket?.send(JSON.stringify(websocketParams));
        });
        socket.addEventListener("message", handleMessage);
        return () => {
            liveGenRef.current += 1;
            socket?.close();
            socket = null;
        };
    }, [enabled, liveSubscribe, caughtUp, filter, applyLiveRows]);

    return {
        rows,
        isScanning,
        isLoadingNewer,
        isNewerPaused,
        newestEvictedEpoch,
        newerPrependEpoch,
        newerAddedCount,
        oldestLoadedEpoch,
        moreAvailable,
        scannedThrough,
        capped,
        error,
        loadOlder,
        cancel,
        reload,
        cancelNewer,
        resumeNewer,
        goToNewest,
        goToOldest: () => void goToOldest(),
    };
}

export default useGradualRecordLogs;
